import autoBind from 'auto-bind'
import {getCountryData} from 'countries-list'
import {
  ApiBase,
  formatExchange,
  formatBonus,
  formatWithdrawal,
  formatDeposit,
  capitalize,
} from '@components/lib'

import {TokenInfo} from 'constData'
import {formatDate, formatDateWithoutTime, formateFloatCurrency, formatCurrency} from 'utils'

// const getMethodUri = (uri, instanceId = null) => `/${uri}${instanceId ? `/${instanceId}` : ''}`

export default class ApiMethods extends ApiBase {
  constructor(props) {
    super(props)
    autoBind(this)
  }

  // AUTH API

  async login(params) {
    return await this.post('/auth/login', params)
  }

  async signUpInitial(params) {
    return await this.post('/auth/signup', params)
  }

  async resendEmailConfirmation() {
    return this.get('/auth/email_confirmation')
  }

  async emailConfirm(params) {
    return await this.post('/auth/email_confirmation', params)
  }

  async passwordResetRequest(params) {
    return this.post('/auth/reset_password', params)
  }

  async passwordResetConfirm(params) {
    return this.put('/auth/reset_password', params)
  }

  async changePassword(data) {
    return this.post(`/auth/change_user_password`, data)
  }

  async createNewMfa() {
    return this.get('/auth/otp')
  }

  async proceedMfa(params) {
    return this.post('/auth/otp', params)
  }

  async uploadFile(data) {
    const {url} = await this.post(`/files/upload`, data, false, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    })
    return url
  }

  /**
   * @todo: Need to change this api
   */
  async logout() {
    return this.delete('/auth/logout')
  }

  /**
   * @todo: Need to check this api response
   */
  async profile() {
    return await this.get('/auth/profile')
  }

  /**
   * @todo: Need to implement this fetaure in python project
   */
  async connectMinebaseTeam() {
    return await this.post(`/core/team`)
  }

  async getBalance() {
    const {amount} = await this.get(`/account_asset/total_balance`)
    return amount
  }

  /**
   * @todo: Need to implement this feature in python project
   */
  //  Delete user temporary
  async deleteUser({userId, ...params}) {
    return await this.delete(`/users/${userId}`, params)
  }

  // OTHER

  async getUserBonuses({t, ...params}) {
    const {user_bonuses, ...rest} = await this.get(`/user_bonuses`, params)
    return {
      data: user_bonuses.map(formatBonus(t)),
      ...rest,
    }
  }

  async getQuotes() {
    return await this.get(`/gas/crypto_exchange_rate`)
  }

  async submitTokenWithdrawal({userId, ...data}) {
    const {withdrawal} = await this.post(`/account_asset/withdraw`, data)
    return withdrawal
  }

  async getWithdrawEligibility(data) {
    return await this.get(`/core/withdrawals/fees`, data)
  }

  async confirmWithdrawal(data) {
    return await this.post(`/core/withdrawals/confirm`, data)
  }

  async getAsset({asset}) {
    const {results} = await this.get(`/account_asset/wallets`, {asset})
    return results.find(item => item.asset === asset) ?? null
  }

  async generateAsset({asset}) {
    return this.post(`/account_asset/wallets`, {asset})
  }

  // Legacy wallets
  async legacyWallets({asset, ...params}) {
    const {results, ...rest} = await this.get(`/minebase/old_assets`, {asset, ...params})

    let modifiedResults = results
    let currentDate = new Date().setHours(0, 0, 0, 0)
    let targetDate = new Date(2024, 4, 10).setHours(0, 0, 0, 0)
    // Note: Months are zero-based, so 4 represents May

    if (asset && (asset === 'GNS' || asset === 'V999') && currentDate <= targetDate) {
      if (asset) {
        return results.find(item => item.asset === asset) ?? null
      }
    }
    return (
      modifiedResults && {
        results: modifiedResults.map(item => ({
          ...item,
          total:
            (item.asset === 'V999' || item.asset === 'GNS') && currentDate > targetDate
              ? 0
              : item.total,
          available:
            (item.asset === 'V999' || item.asset === 'GNS') && currentDate > targetDate
              ? 0
              : item.available,
        })),
        ...rest,
      }
    )
  }

  async getAssetDeposits({t, ...params}) {
    const {results, ...rest} = await this.get(`/core/deposits`, params)
    return {
      results: results.map(formatDeposit(t)),
      ...rest,
    }
  }

  async getAssetWithdrawals({t, ...params}) {
    const {results, ...rest} = await this.get(`/core/withdrawals`, params)
    return {
      results: results.map(formatWithdrawal(t)),
      ...rest,
    }
  }

  async getAssetExchanges({assetId, t, ...params}) {
    if (assetId) {
      const {exchanges, ...rest} = await this.get(`/account_assets/${assetId}/exchanges`, params)
      return {
        data: exchanges.map(formatExchange(t)),
        ...rest,
      }
    }
    return {data: [], total: 0}
  }

  /**
   * Notification
   */

  async getNotifications(params) {
    return this.get('/notifications/lists', {
      sort_by: 'deliver_at',
      sort_order: 'desc',
      ...params,
    })
  }

  async getUnreadNotificationsCount() {
    const {count} = await this.get('/notifications/lists', {
      is_read: 0,
    })
    return count
  }

  async notificationRead({id}) {
    return await this.get(`/notifications/lists/${id}/make_read`)
  }

  async getWithdrawalLimits() {
    const {results} = await this.get('/core/withdrawals_limits')
    return results
  }

  async getDepositLimits() {
    const {results} = await this.get('/core/deposits_limits')
    return results
  }

  async getWithdrawalFees() {
    const {results} = await this.get('/core/withdrawals_fees')
    return results
  }
  async getMbaseWithdrawFees() {
    return this.get('/withdrawals_fees/mbase_fees')
  }

  // eslint-disable-next-line class-methods-use-this
  async isCryptoApiWorking() {
    await new Promise(r => setTimeout(r, 150))
    return true
  }

  async submitContactUsForm(data) {
    return this.post(`/contact_us/submit`, data)
  }

  async mailSubscription({userId}) {
    return await this.post(`/users/${userId}/subscription`)
  }

  /**
   * @todo: Need to change this api
   */
  async unsubscribeWithToken(params) {
    return this.post(`/unsubscribe_with_token`, params)
  }

  async getTermsCondition() {
    return this.get(`terms_conditions/index`)
  }

  async postTermsCondition(terms_conditions_id) {
    return this.post(`/terms_conditions/process_users_terms`, {
      terms_conditions_id: terms_conditions_id,
    })
  }

  async showPopUp(params) {
    return this.get(`/users/popup/`, params)
  }

  async disablePopUp(data) {
    return this.post(`/users/popup/`, data)
  }

  async tokenTransfer(data) {
    return await this.post(`/minebase/process_to_third_convert_token`, data)
  }

  async getTokenConvertEligibility({userId, ...params}) {
    return await this.get(`/minebase/get_eligibility_to_third_convert_token`, params)
  }

  async getMbaseBalance({userId}) {
    return await this.get(`/users/${userId}/get_token_amount`, {
      asset: TokenInfo.MBASE.apiId,
    })
  }

  async userMbaseBonus() {
    return await this.get(`/minebase/period_of_time_code`)
  }

  async createWalletAddress({address, type}) {
    return await this.post(`/minebase/own_wallet`, {
      wallet_address: address,
      asset: type,
    })
  }
  async deleteWalletAddress({address, type}) {
    return await this.post(`/minebase/delete_own_wallet`, {
      wallet_address: address,
      asset: type,
    })
  }

  async getUserAddress({asset_id, ...params}) {
    const {results, ...rest} = await this.get(`/minebase/mining_wallets`, {...params, asset_id})
    return (
      results && {
        results: results.map(item => ({
          ...item,
          counted_tx_fees: formateFloatCurrency(
            'USDT',
            asset_id === TokenInfo.XON.apiId ? item.xon_mining_balance : item.counted_tx_fees,
          ),
          verified: item.verified ? 'Yes' : 'No',
          created_at: formatDate(item.created_at),
          assign_time: formatDate(item.assign_time),
          end_time: formatDate(item.assign_time),
          action: item?.is_blocked,
        })),
        ...rest,
      }
    )
  }

  async getConvertedTokenList({userId, asset, ...params}) {
    const {results, ...rest} = await this.get(`/minebase/token_convert_list`, {
      ...params,
      asset,
    })
    return (
      results && {
        results: results.map(item => ({
          ...item,
          created_at: formatDate(item.created_at),
          asset_amount: parseFloat(item.asset_amount).toFixed(6),
          convert_amount: parseFloat(item.convert_amount).toFixed(6),
        })),
        ...rest,
      }
    )
  }

  async getUsersPeriodOfTime(params) {
    const {results, ...rest} = await this.get(`/minebase/period_of_time`, {
      ...params,
    })
    return (
      results && {
        results: results.map(item => ({
          ...item,
          asset_amount: formateFloatCurrency(TokenInfo.MBASE.apiId, item.asset_amount),
          created_at: formatDate(item.created_at),
          updated_at: formatDate(item.updated_at),
        })),
        ...rest,
      }
    )
  }

  async sponsorBonus(params) {
    const {results, ...rest} = await this.get(`/minebase/asset_transactions`, {
      ...params,
    })

    return (
      results && {
        results: results.map(item => ({
          ...item,
          account: item?.account?.account,
          created_at: formatDate(item?.created_at),
          amount: formateFloatCurrency(TokenInfo.MBASE.apiId, item?.amount),
        })),
        ...rest,
      }
    )
  }

  async producedToken(params) {
    const {results, ...rest} = await this.get(`/minebase/mining_transactions`, params)

    return (
      results && {
        results: results.map(item => ({
          ...item,
          amount: formateFloatCurrency(TokenInfo.MBASE.apiId, item.amount),
          created_at: formatDate(item.created_at),
          type: item.user_status
            ? 'User status'
            : item.connected_wallet
              ? 'Interconnect'
              : 'Automatic',
        })),
        ...rest,
      }
    )
  }

  async ctpPrice() {
    return await this.get('minebase/get_mbase_price')
  }

  async submitRewardBonusCode(data) {
    return await this.post(`/minebase/token_converted`, data)
  }

  async checkSponsor(id) {
    return await this.get(`/users/check_sponsor?token=${id}`, {})
  }

  async getVerifierWallets() {
    const results = await this.get(`/minebase/verifier_wallets`, {})
    return results
  }

  async getPromotionBonus(params) {
    const {results, ...rest} = await this.get('/minebase/all_promotion_bonus', params)
    return (
      results && {
        results: results?.map((item, index) => ({
          ...item,
          account_id: item?.account?.account,
          created_at: formatDate(item.created_at),
          amount: formateFloatCurrency(TokenInfo.MBASE.apiId, item.amount),
          id: index + 1,
        })),
        ...rest,
      }
    )
  }

  async getDownlineUsers({userId, ...params}) {
    const {results, ...rest} = await this.get(`/user_detail/downline_users`, params)
    return (
      results && {
        results: results.map((item, index) => ({
          ...item,
          created_at: formatDate(item.created_at),
          full_name: item.first_name + ' ' + item.last_name,
          user_country: item.user_country
            ? getCountryData(item.user_country.toUpperCase()).name
            : null,
          is_active: item.is_active ? 'Activated' : 'Not Activated',
        })),
        ...rest,
      }
    )
  }

  async getMbaseWithdrawalFees({asset, ...params}) {
    const {results, ...rest} = await this.get(`/core/withdraw_earned_fees`, {
      ...params,
      fee_asset: asset,
    })

    return (
      results && {
        results: results.map(item => ({
          ...item,
          fee_in_asset: formateFloatCurrency(asset, item.fee_in_asset),
          created_at: formatDate(item.created_at),
        })),
        ...rest,
      }
    )
  }

  async getUserPromotionBonus(params) {
    const {results, ...rest} = await this.get(`/minebase/user_promotion_bonus`, {
      ...params,
    })
    return (
      results && {
        results: results?.map((item, index) => ({
          ...item,
          created_at: formatDate(item.created_at),
          amount: formateFloatCurrency(TokenInfo.MBASE.apiId, item.amount),
          id: index + 1,
        })),
        ...rest,
      }
    )
  }

  // interconnect wallets
  async checkInterConnectAllowed() {
    return await this.get(`/interconnect/check_allowed`)
  }

  async getInterConnectedWallets(params) {
    const {results, ...rest} = await this.get(`/interconnect/connected_wallets`, params)

    return (
      results && {
        results: results.map((item, index) => ({
          ...item,
          start_date: formatDate(item.start_date),
          end_date: formatDate(item.end_date),
          serial_no: index + 1,
          months: `${item.months} months`,
          paid_amount: formateFloatCurrency(item.payment_type, item.paid_amount),
          created_at: formatDate(item.created_at),
        })),
        ...rest,
      }
    )
  }

  async getInterConnectedMiningWallets(params) {
    const {results, ...rest} = await this.get(`/interconnect/assigned_wallets`, params)

    return (
      results && {
        results: results.map(item => ({
          ...item,
          counted_tx_fees: formateFloatCurrency('USDT', item.counted_tx_fees),
          verified: item.verified ? 'Yes' : 'No',
          created_at: formatDate(item.created_at),
          assign_time: formatDate(item.assign_time),
          end_time: formatDate(item.assign_time),
        })),
        ...rest,
      }
    )
  }

  async getInterConnectCurrency(params) {
    return await this.get(`/interconnect/get_currency`, params)
  }

  async purchaseInteConnectWallets({paymentMethod, ...params}) {
    return await this.post(
      `/interconnect/process_purchase_interconnect_package`,
      {
        payment_method: paymentMethod,
        ...params,
      },
      false,
      {timeout: 180000},
    )
  }

  async interconnectVouchers({userId, ...params}) {
    const {results, ...rest} = await this.get(`/interconnect/all_coupon_list`, params)
    return (
      results && {
        results: results.map(item => ({
          ...item,
          created_at: formatDate(item.created_at),
          used_date: formatDate(item.used_date),
        })),
        ...rest,
      }
    )
  }

  // purchase mbase token
  async getPurchaseTokenCurrency(params) {
    return await this.get(`/minebase/get_purchase_mbase_token_currency`, params)
  }

  /*  async purchaseToken(params) {
    return await this.post(`/minebase/purchase_mbase_token`, params)
  } */

  async getPurchasedTokens(params) {
    const {results, ...rest} = await this.get('/minebase/buy_token_purchased', params)

    return (
      results && {
        results: results.map(item => ({
          ...item,
          tx_time: formatDate(item.tx_time),
          created_at: formatDate(item.created_at),
          paid_amount: formateFloatCurrency(item?.payment_type, item?.paid_amount),
          amount: formateFloatCurrency(item.asset_id, item.amount),
          mbase_rate: parseFloat(item.mbase_rate).toFixed(6),
          eth_dollar_rate: parseFloat(item.eth_dollar_rate).toFixed(6),
        })),
        ...rest,
      }
    )
  }

  // Avatar package purchase
  async getProductPackages(params) {
    const data = await this.get(`/products/packages`, params)
    return (
      data &&
      data.map(item => ({
        ...item,
        price: item?.usd_price,
        extraPrice: item?.MBASE,
        priceCurrency: 'USD',
        extraPriceCurrency: 'MBASE',
      }))
    )
  }
  async purchasePackage(data) {
    return await this.post(`/products/purchase_package`, data)
  }

  async getSharedPackages(params) {
    const data = await this.get(`/products/get_packages_share`, params)

    return (
      data &&
      data.map(item => ({
        ...item,
        priceText: 'Your purchased packages',
        price: item.purchase_count,
      }))
    )
  }

  async getPurchasedPackages(params) {
    const {results, ...rest} = await this.get(`/minebase/avatar_purchase`, params)

    return (
      results && {
        results: results.map(item => ({
          ...item,
          product_name: [
            {id: 1, product_name: 'Single Packages'},
            {id: 2, product_name: 'Multi Packages'},
            {id: 3, product_name: 'Premium Packages'},
          ].find(product => item?.product_id === product?.id)?.product_name,
          paid_mbase: formateFloatCurrency(TokenInfo.MBASE.apiId, item.paid_mbase),
          paid_usdt: formateFloatCurrency(item?.payment_type, item.paid_usdt),
          created_at: formatDate(item.created_at),
        })),
        ...rest,
      }
    )
  }

  async getPurchasedPackagesSponsorBonus({...params}) {
    const {results, ...rest} = await this.get(`/account_asset/sponsor_bonus_list`, params)
    return (
      results && {
        results: results.map(item => ({
          ...item,
          amount: formateFloatCurrency('USDT', item.amount),
          created_at: formatDate(item.created_at),
        })),
        ...rest,
      }
    )
  }

  async getAnnualPackageList({userId}) {
    const {apy_package_list} = await this.get(`/users/${userId}/apy_package_list`)
    return (
      apy_package_list &&
      apy_package_list.map(item => ({
        ...item,
        price: item?.usd_price,
        priceCurrency: 'USD',
      }))
    )
  }

  // Annual percentage yield apis goes here...
  async annualPackage({userId, ...data}) {
    return await this.post(`/users/${userId}/buy_apy_package`, data)
  }

  async getAnnualPurchasedPackages(params) {
    const {results, ...rest} = await this.get(`/products/annual_purchases`, params)

    return (
      results && {
        results: results.map(item => ({
          ...item,
          product_name: [
            {id: 4, product_name: 'Minebase APY 250'},
            {id: 5, product_name: 'Minebase APY 750'},
            {id: 6, product_name: 'Minebase APY 2K'},
            {id: 7, product_name: 'Minebase APY 5k'},
            {id: 8, product_name: 'Minebase APY 10k'},
          ].find(product => item?.product_id === product?.id)?.product_name,
          worth_of_tokens: formatCurrency(
            [
              {id: 4, worth_of_tokens: '500'},
              {id: 5, worth_of_tokens: '2250'},
              {id: 6, worth_of_tokens: '8000'},
              {id: 7, worth_of_tokens: '25000'},
              {id: 8, worth_of_tokens: '60000'},
            ].find(product => item?.product_id === product?.id)?.worth_of_tokens,
            {currency: 'USD'},
          ),
          payout_amount: item.payout_amount
            ? formateFloatCurrency(TokenInfo.MBASE.apiId, item.payout_amount)
            : '-',
          payout_date: formatDateWithoutTime(item.payout_date),
          end_date: formatDateWithoutTime(item.end_date),
          created_at: formatDate(item.created_at),
          paid_amount: formateFloatCurrency(item?.payment_type, item.paid_amount),
          payout_type: capitalize(item?.payout_type?.replaceAll('_', ' ')),
        })),
        ...rest,
      }
    )
  }

  async payoutType(data) {
    return await this.put(`/products/update_apy_payout_type`, data)
  }

  // Global marketing plan api goes here....
  async getGlobalPackageList(params) {
    const {results} = await this.get(`/products/packages`, params)
    return (
      results &&
      results.map(item => ({
        ...item,
        price: item?.usd_price,
        priceCurrency: 'USD',
        mbase_price: parseFloat(item?.mbase_price).toFixed(2),
      }))
    )
  }

  async buyGlobalPackage(data) {
    const response = await this.post(`/products/purchase_package`, data)
    return {
      ...response,
      product_id: data.product_id,
    }
  }

  async getGlobalPurchasedPackages(params) {
    const {results, ...rest} = await this.get(`/products/marketing_purchases`, params)
    return (
      results && {
        results: results.map(item => ({
          ...item,
          product_name: [
            {id: 9, product_name: '100$ Star Package'},
            {id: 10, product_name: '500$ Star Package'},
            {id: 11, product_name: '1000$ Star Package'},
            {id: 12, product_name: '3000$ Star Package'},
            {id: 13, product_name: '5000$ Star Package'},
            {id: 14, product_name: 'APY Star 1'},
            {id: 15, product_name: 'APY Star 2'},
            {id: 16, product_name: 'APY Star 3'},
            {id: 17, product_name: 'APY Star 4'},
            {id: 18, product_name: 'APY Star 5'},
          ].find(product => item?.product_id === product?.id)?.product_name,
          end_date: formatDateWithoutTime(item.end_date),
          created_at: formatDate(item.created_at),
          worth_of_tokens: item?.is_gcp_apy
            ? formatCurrency(
                [
                  {id: 14, worth_of_tokens: '150'},
                  {id: 15, worth_of_tokens: '1250'},
                  {id: 16, worth_of_tokens: '3000'},
                  {id: 17, worth_of_tokens: '12000'},
                  {id: 18, worth_of_tokens: '25000'},
                ].find(product => item?.product_id === product?.id)?.worth_of_tokens,
                {currency: 'USD'},
              )
            : null,
          paid_amount: formateFloatCurrency(item?.payment_type, item.paid_amount),
          minebase_rate: parseFloat(item?.minebase_rate).toFixed(6),
          payment_method:
            item?.payment_method && item?.payment_method !== 'converted'
              ? 'Purchase'.toUpperCase()
              : item?.payment_method.toUpperCase(),
          minebase_amount: !item?.is_gcp_apy
            ? formateFloatCurrency(TokenInfo.MBASE.apiId, item?.minebase_amount)
            : null,
        })),
        ...rest,
      }
    )
  }

  async gmpShortProfit({userId, ...data}) {
    return await this.post(`/products/update_short_profit`, data)
  }
  async userStarPackagesProfit(params) {
    const {results, ...rest} = await this.get(`/minebase/short_term_profit/history`, params)
    return (
      results && {
        results: results.map((item, index) => ({
          ...item,
          serial_no: index + 1,
          mbase_latest_rate: parseFloat(item.mbase_latest_rate).toFixed(6),
          mbase_prior_rate: parseFloat(item.mbase_prior_rate).toFixed(6),
          mbase_current_rate: parseFloat(item.mbase_current_rate).toFixed(6),
          product_price: formatCurrency(item.product_price, {currency: 'USD'}),
          profit_payable: formateFloatCurrency(TokenInfo.MBASE.apiId, item.profit_payable),
          mbase_amount: formateFloatCurrency(TokenInfo.MBASE.apiId, item.mbase_amount),
          profit_in_fiat: formatCurrency(item.profit_in_fiat, {currency: 'USD'}),
          created_at: formatDate(item.created_at),
        })),
        ...rest,
      }
    )
  }

  async getUserIncentiveAwarded(params) {
    const {results, ...rest} = await this.get(`/incentives/incentive_awarded`, params)
    return (
      results && {
        results: results.map(item => ({
          ...item,
          awarded_asset: item?.incentive_type === 1 ? TokenInfo.MBASE.apiId : 'USDT',
          awarded_amount: formateFloatCurrency(
            item?.incentive_type === 1 ? TokenInfo.MBASE.apiId : 'USDT',
            item.awarded_amount,
          ),
          created_at: formatDate(item.created_at),
        })),
        ...rest,
      }
    )
  }

  async getUserStatus({...params}) {
    const results = await this.get('/minebase/user_status', {...params})
    return results
  }

  async userStatus({userId, ...params}) {
    return await this.post(`/minebase/user_status`)
  }

  async getUserStatusWallets(params) {
    const {results, ...rest} = await this.get('/minebase/status_mining_wallets', params)
    return (
      results && {
        results: results.map(item => ({
          ...item,
          counted_tx_fees: formateFloatCurrency(TokenInfo.MBASE.apiId, item.counted_tx_fees),
          verified: item.verified ? 'Yes' : 'No',
          created_at: formatDate(item.created_at),
          assign_time: formatDate(item.assign_time),
          end_time: formatDate(item.assign_time),
        })),
        ...rest,
      }
    )
  }

  async getUserUnilevelTree(params) {
    return await this.get(`/user_tree/user_unilevel_tree`, params)
  }

  async getUserCtpBooster(params) {
    const {results, ...rest} = await this.get(`/incentives/ctp_booster_awarded`, params)

    return (
      results && {
        results: results.map(item => ({
          ...item,
          amount: formateFloatCurrency(TokenInfo.MBASE.tokenName, item.amount),
          amount_given: formateFloatCurrency(TokenInfo.MBASE.tokenName, item.amount_given),
          created_at: formatDate(item.created_at),
        })),
        ...rest,
      }
    )
  }

  async getFastStartBonus(params) {
    const {results, ...rest} = await this.get(`/incentives/fast_start_bonus`, params)

    return (
      results && {
        results: results.map((item, index) => ({
          ...item,
          serial_no: index + 1,
          amount: formateFloatCurrency(TokenInfo.MBASE.tokenName, item.amount),
          last_given_amount: formateFloatCurrency(
            TokenInfo.MBASE.tokenName,
            item.last_given_amount,
          ),
          created_at: formatDate(item.created_at),
        })),
        ...rest,
      }
    )
  }

  // Global compensation plan (GCP) api section
  async compensationDownline(params) {
    const {results, ...rest} = await this.get(`/gcp/users_compensation_plan`, params)

    return (
      results && {
        results: results.map(item => ({
          ...item,
          created_at: formatDate(item.created_at),
        })),
        ...rest,
      }
    )
  }

  async compensationHistory({userId, ...params}) {
    const {results, ...rest} = await this.get(`/gcp/compensation_plan_history`, params)

    return (
      results && {
        results: results.map(item => ({
          ...item,
          created_at: formatDate(item.created_at),
        })),
        ...rest,
      }
    )
  }

  async compensationCreditHistory({userId, ...params}) {
    const {compensation_credit_histories, ...rest} = await this.get(
      `/users/${userId}/compensation_credit_histories`,
      params,
    )

    return (
      compensation_credit_histories && {
        data: compensation_credit_histories.map(item => ({
          ...item,
          created_at: formatDate(item.created_at),
        })),
        ...rest,
      }
    )
  }

  async getGCPCommissions(params) {
    const {results, ...rest} = await this.get(`/gcp/users_commission_history`, params)

    return (
      results && {
        results: results.map(item => ({
          ...item,
          payout_amount: formateFloatCurrency('USDT', item.payout_amount),
          business_name: item?.business_name.replaceAll('_', ' ').toUpperCase(),
          updated_at: formatDate(item.updated_at),
        })),
        ...rest,
      }
    )
  }

  async myDownlineDetails() {
    return await this.get(`/gcp/my_downline_details`)
  }

  async centerLevelDetails() {
    const data = await this.get(`/gcp/center_level_details`)

    return (
      data &&
      data.map(item => ({
        ...item,
        id: item?.id?.replaceAll('_', ' ').toLowerCase(),
      }))
    )
  }

  async lastCommissions() {
    return await this.get(`/gcp/business_level_history`)
  }

  async lastCommissionsDateRange() {
    const data = await this.get(`/gcp/last_commission_range`)
    return {
      ...data,
      start_date: formatDateWithoutTime(data.start_date),
      end_date: formatDateWithoutTime(data.end_date),
    }
  }

  async globalMarketingCurrentShared(params) {
    const data = await this.get(`/minebase/total_share`, params)
    return data
  }

  async directSales(params) {
    const {results, ...rest} = await this.get(`/products/direct_sales`, params)

    return (
      results && {
        results: results.map(item => ({
          ...item,
          paid_amount: item.paid_amount
            ? formateFloatCurrency(item.payment_type, item.paid_amount)
            : item.paid_usdt && formateFloatCurrency(item.payment_type, item.paid_usdt),
          paid_mbase: item.paid_mbase && formateFloatCurrency(item.payment_type, item.paid_mbase),
          payment_type:
            Number(item.paid_amount) || Number(item.paid_usdt) ? item.payment_type : null,
          created_at: formatDate(item.created_at),
        })),
        ...rest,
      }
    )
  }

  // Rising fees api section
  async getStakedHolders(params) {
    const {results, ...rest} = await this.get(`/minebase/stake_holders`, {
      ...params,
    })

    return (
      results && {
        results: results.map((item, index) => ({
          ...item,
          serial_no: index + 1,
          created_at: formatDate(item?.created_at),
          release_date: formatDateWithoutTime(item?.release_date),
          stake_balance: formateFloatCurrency(TokenInfo.MBASE.apiId, item?.stake_balance),
        })),
        ...rest,
      }
    )
  }

  async stakeToken({userId, ...data}) {
    return await this.post(`/minebase/post_stake_holders`, data)
  }

  async christmasIncentive(params) {
    const {results, ...rest} = await this.get(`/incentives/christmas_incentives`, params)

    return (
      results && {
        results: results.map(item => ({
          ...item,
          share: parseInt(item.share),
          payout_amount: formateFloatCurrency('USDT', item.payout_amount),
          created_at: formatDate(item.created_at),
        })),
        ...rest,
      }
    )
  }

  // Metaverse land
  async metaverseLand(params) {
    const {results} = await this.get(`/products/packages`, params)
    return results
  }

  async purchaseMetaverseLand(data) {
    return await this.post(`/products/purchase_metaverse_land`, data)
  }

  async purchasedMetaverseLand(params) {
    const {results, ...rest} = await this.get(`/products/metaverse_land_purchases`, params)

    return (
      results && {
        results: results.map(item => ({
          ...item,
          product_name: 'Land Shares',
          paid_amount: formateFloatCurrency(TokenInfo.MBASE.apiId, item.paid_amount),
          land_share: parseFloat(item.land_share),
          created_at: formatDate(item.created_at),
        })),
        ...rest,
      }
    )
  }

  async purchasedVime(params) {
    const {results, ...rest} = await this.get(`/products/purchased_vime_package`, params)

    return (
      results && {
        results: results.map(item => ({
          ...item,
          product_name: [
            {id: 20, product_name: 'Vime Package 1'},
            {id: 21, product_name: 'Vime Package 2'},
            {id: 22, product_name: 'Vime Package 3'},
            {id: 23, product_name: 'Vime Package 4'},
            {id: 24, product_name: 'Vime Package 5'},
          ].find(product => item?.product_id === product?.id)?.product_name,
          end_date: formatDateWithoutTime(item.end_date),
          created_at: formatDate(item.created_at),
          paid_amount: formateFloatCurrency(item?.payment_type, item.paid_amount),
          minebase_rate: parseFloat(item?.minebase_rate).toFixed(6),
          payment_method:
            item?.payment_method && item?.payment_method !== 'converted'
              ? 'Purchase'.toUpperCase()
              : item?.payment_method.toUpperCase(),
        })),
        ...rest,
      }
    )
  }

  async purchasedVimeMbase(params) {
    const {results, ...rest} = await this.get(`/products/purchased_veelive_package`, params)

    return (
      results && {
        results: results.map(item => ({
          ...item,
          product_name: [
            {id: 25, product_name: 'VIME with Mbase Token'},
            {id: 26, product_name: 'Sales Consultant'},
            {id: 27, product_name: 'Sales Manager'},
            {id: 28, product_name: 'Sales Director'},
            {id: 29, product_name: 'P.R Assistant'},
            {id: 30, product_name: 'P.R Manager'},
            {id: 31, product_name: 'P.R Director'},
          ].find(product => item?.product === product?.id)?.product_name,
          end_date: formatDateWithoutTime(item.end_date),
          created_at: formatDate(item.created_at),
          paid_amount: formateFloatCurrency(item?.payment_type, item.paid_amount),
          minebase_rate: parseFloat(item?.minebase_rate).toFixed(6),
          quantity: parseFloat(item?.quantity).toFixed(0),
          payment_method:
            item?.payment_method && item?.payment_method !== 'converted'
              ? 'Purchase'.toUpperCase()
              : item?.payment_method.toUpperCase(),
        })),
        ...rest,
      }
    )
  }

  async purchasedCharacter(params) {
    const {results, ...rest} = await this.get(`/products/purchased_veelive_character`, params)

    return (
      results && {
        results: results.map(item => ({
          ...item,
          product_name: [
            {id: 32, product_name: 'Explorer'},
            {id: 33, product_name: 'Visionary'},
            {id: 34, product_name: 'Meta Titan'},
            {id: 35, product_name: 'Alpha Prime'},
            {id: 36, product_name: 'Star Monarch'},
            {id: 37, product_name: 'Supreme Overlord'},
            {id: 38, product_name: 'Master Universe'},
          ].find(product => item?.product === product?.id)?.product_name,
          end_date: formatDateWithoutTime(item.end_date),
          created_at: formatDate(item.created_at),
          paid_amount: formateFloatCurrency(item?.payment_type, item.paid_amount),
          minebase_rate: parseFloat(item?.minebase_rate).toFixed(6),
          quantity: parseFloat(item?.quantity).toFixed(0),
          payment_method:
            item?.payment_method && item?.payment_method !== 'converted'
              ? 'Purchase'.toUpperCase()
              : item?.payment_method.toUpperCase(),
        })),
        ...rest,
      }
    )
  }

  async sharedMetaverseLand() {
    return await this.get(`/products/user_metaverse_land_share`)
  }

  async metaverseLandSharePrice() {
    return await this.get('/products/metaverse_land_share_price')
  }

  // Legacy Products
  async legacyProductsSponsorBonuses({...params}) {
    const {results, ...rest} = await this.get(`/account_asset/sponsor_bonus_list`, params)
    return (
      results && {
        results: results.map(item => ({
          ...item,
          bonus_type: {PPB: 'Avatar', APY: 'APY', GMPAPYB: 'APY Star', GMPB: 'Star Package'}[
            item.bonus_type
          ],
          amount: formateFloatCurrency('USDT', item.amount),
          created_at: formatDate(item.created_at),
        })),
        ...rest,
      }
    )
  }

  async apyPayout(params) {
    const {results, ...rest} = await this.get(`/products/apy_payouts`, params)
    return (
      results && {
        results: results.map((item, index) => ({
          ...item,
          serial_no: index + 1,
          usd_amount: parseFloat(item.usd_amount).toFixed(6),
          mbase_amount: formateFloatCurrency(TokenInfo.MBASE.apiId, item.mbase_amount),
          mbase_rate: parseFloat(item.mbase_rate).toFixed(6),
          prev_mbase_rate: parseFloat(item.prev_mbase_rate).toFixed(6),
          created_at: formatDate(item.created_at),
          end_date: formatDate(item.end_date),
        })),
        ...rest,
      }
    )
  }

  async apyStarPayout(params) {
    const {results, ...rest} = await this.get(`/products/apy_star_payouts`, params)
    return (
      results && {
        results: results.map((item, index) => ({
          ...item,
          serial_no: index + 1,
          created_at: formatDate(item.created_at),
          end_date: formatDate(item.end_date),
        })),
        ...rest,
      }
    )
  }
}
